/**
 * Форма создания брони «+ Бронь» (manager v3 §4.1 — шахматка). Менеджер
 * заводит проживание: номер (из текущей сетки), даты заезд→выезд через
 * зелёный календарь доступности, фамилия и имя гостя. POST /v1/stays.
 *
 * Правки Артёма (бронь):
 *   1) Поля ручного «Тариф за ночь» НЕТ — тариф = base_price_kopecks типа
 *      выбранного номера (показываем «Цена за сутки» только справочно).
 *   2) Даты — не нативный <input type=date>, а зелёный календарь доступности
 *      (AvailabilityCalendar): зелёные = свободные ночи выбранного номера,
 *      занятые/прошедшие — disabled; диапазон [заезд,выезд) без занятых ночей.
 *
 * Бэк (internal/stay): требует room_id, guest_user_id (UUID), from, to,
 * rate_kopecks, currency, source. guest_user_id генерируем на клиенте —
 * бэк лениво провижнит tenant-профиль гостя по нему (fix #283) и пишет
 * фамилию/имя из формы, поэтому в календаре сразу видна фамилия гостя.
 *
 * Цену типа берём из внутреннего реестра /v1/rooms/types (RoomTypeView имеет
 * base_price_kopecks; публичный /room-types отдаёт только агрегаты min/max).
 * Форма видна только менеджеру (canManage) — доступ к внутреннему реестру есть.
 *
 * i18n: все строки русские. Мобайл 375: поля в один столбец (.hsc-stack).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Card } from '@hsc/ui';
import type { CalendarRoom } from '@hsc/types';
import { useMe } from '../../shared/api/useMe.js';
import { formatMoney } from '../../lib/fleet-format.js';
import { useCreateStay } from '../stays/useStays.js';
import { useRoomTypesAdmin } from './useRooms.js';
import { AvailabilityCalendar } from './AvailabilityCalendar.js';

/** RFC3339 (полночь UTC) из YYYY-MM-DD для контракта бэка. */
function toRfc3339(date: string): string {
  return `${date}T00:00:00Z`;
}

/** Генерирует UUID v4 (crypto.randomUUID есть во всех целевых браузерах). */
function newGuestId(): string {
  return crypto.randomUUID();
}

export interface BookingFormProps {
  rooms: CalendarRoom[];
  typeName: Map<string, string>;
  /** Дата, открытая в сетке (YYYY-MM-DD) — префилл заезда. */
  defaultFrom: string;
  onClose: () => void;
  /** По успеху — закрыть форму (календарь инвалидируется хуком). */
  onCreated: () => void;
}

export function BookingForm({
  rooms,
  typeName,
  defaultFrom,
  onClose,
  onCreated,
}: BookingFormProps): JSX.Element {
  const create = useCreateStay();
  const me = useMe();
  const typesQ = useRoomTypesAdmin(me.data?.tenant_id);

  const [roomId, setRoomId] = useState(rooms[0]?.id ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');

  const selectedRoom = useMemo(
    () => rooms.find((r) => r.id === roomId),
    [rooms, roomId],
  );

  // type_id → base_price_kopecks (внутренний реестр типов номеров).
  const priceByType = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of typesQ.data ?? []) m.set(t.id, t.base_price_kopecks);
    return m;
  }, [typesQ.data]);

  const currencyByType = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of typesQ.data ?? []) m.set(t.id, t.currency || 'RUB');
    return m;
  }, [typesQ.data]);

  const nightlyKopecks = selectedRoom ? priceByType.get(selectedRoom.type_id) : undefined;
  const currency = (selectedRoom && currencyByType.get(selectedRoom.type_id)) || 'RUB';

  // При смене номера сбрасываем выбранные даты — занятость другая.
  useEffect(() => {
    setFrom('');
    setTo('');
  }, [roomId]);

  const datesValid = from !== '' && to !== '' && to > from;
  const canSubmit =
    roomId !== '' &&
    datesValid &&
    lastName.trim().length > 0 &&
    nightlyKopecks !== undefined &&
    !create.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit || nightlyKopecks === undefined) return;
    create.mutate(
      {
        room_id: roomId,
        guest_user_id: newGuestId(),
        from: toRfc3339(from),
        to: toRfc3339(to),
        rate_kopecks: nightlyKopecks,
        currency,
        source: 'manual',
        guest_last_name: lastName.trim(),
        guest_first_name: firstName.trim(),
      },
      { onSuccess: onCreated },
    );
  };

  const roomOptions = useMemo(
    () =>
      rooms.map((r) => ({
        id: r.id,
        label: `№ ${r.number}${typeName.get(r.type_id) ? ` · ${typeName.get(r.type_id)}` : ''}`,
      })),
    [rooms, typeName],
  );

  // Подпись выбранного диапазона дат для менеджера.
  const dateSummary = useMemo(() => {
    const fmt = (iso: string): string => {
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    };
    if (from === '') return 'Выберите даты в календаре';
    if (to === '') return `Заезд: ${fmt(from)} · выберите дату выезда`;
    return `Заезд: ${fmt(from)} → Выезд: ${fmt(to)}`;
  }, [from, to]);

  return (
    <section data-testid="booking-form">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">Новая бронь</h1>
      </div>

      <Card>
      <form className="hsc-stack" onSubmit={handleSubmit}>
        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="booking-room">
            Номер
          </label>
          <select
            id="booking-room"
            className="ta-select"
            data-testid="booking-room"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            required
          >
            {roomOptions.length === 0 && <option value="">— нет номеров —</option>}
            {roomOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Цена за сутки — справочно, из base_price типа номера (без ввода). */}
        <div className="booking-price" data-testid="booking-price">
          <span className="booking-price__label">Цена за сутки</span>
          <span className="booking-price__value">
            {nightlyKopecks !== undefined
              ? formatMoney(nightlyKopecks, currency)
              : typesQ.isLoading
                ? 'загрузка…'
                : '—'}
          </span>
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="booking-last-name">
            Фамилия гостя
          </label>
          <input
            id="booking-last-name"
            className="ta-input"
            data-testid="booking-last-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Например, Иванов"
            maxLength={60}
            required
          />
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="booking-first-name">
            Имя гостя
          </label>
          <input
            id="booking-first-name"
            className="ta-input"
            data-testid="booking-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Например, Анна"
            maxLength={60}
          />
        </div>

        <div className="ta-field">
          <span className="hsc-input__label">Даты проживания</span>
          <p className="booking-dates-summary" data-testid="booking-dates-summary">
            {dateSummary}
          </p>
          <AvailabilityCalendar
            occupancies={selectedRoom?.occupancies ?? []}
            from={from}
            to={to}
            onPick={({ from: nf, to: nt }) => {
              setFrom(nf);
              setTo(nt);
            }}
          />
        </div>

        {create.isError && (
          <p role="alert" className="fleet-card__err" data-testid="booking-error">
            Не удалось создать бронь. Возможно, номер занят на эти даты — проверьте период.
          </p>
        )}

        {/* Кнопки СНИЗУ: Создать + Назад (canon формы отдельным экраном). */}
        <div className="room-edit__footer">
          <Button type="submit" disabled={!canSubmit} data-testid="booking-submit">
            {create.isPending ? 'Создаём…' : 'Создать бронь'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} data-testid="booking-form-close">
            Назад
          </Button>
        </div>
      </form>
      </Card>
    </section>
  );
}
